/**
 * Event catalog — the published contract between this site and the dashboards.
 *
 * This file is the whole taxonomy. Adding an event means adding one entry
 * here; there is no second place to register it, no switch statement to
 * extend, and no transport to teach. That is the entire reason the catalog
 * exists as data rather than as a set of `trackFoo()` functions.
 *
 * **The list is deliberately short, and deliberately closed.** The previous
 * taxonomy on this site sprawled and then went dark for months without
 * anyone noticing, because nobody could tell which events were supposed to
 * be flowing. Six events that are known-good beat twenty that nobody
 * watches. Web Vitals were considered for this pass and left out on the
 * same reasoning — see docs/backlog.md.
 *
 * Changing an existing entry is a breaking change for a dashboard view.
 * Adding a field is safe; renaming or removing one is not.
 *
 * @module logging/events/catalog
 */

import { z } from 'zod';
import type { Severity } from '../core/types';

// =============================================================================
// SHARED VALUE TYPES
// =============================================================================

/** How a timed or fallible interaction ended. Fixed by the contract. */
const outcomeSchema = z.enum(['succeeded', 'failed', 'cancelled']);

/** Coarse error buckets. These are the categories the error charts slice on;
 *  a finer taxonomy belongs in Cloud Error Reporting, which groups on the
 *  stack trace and does it better than a hand-maintained enum would. */
const errorCategorySchema = z.enum(['auth', 'network', 'validation', 'unknown']);

/** Elapsed time. Integer milliseconds — `performance.now()` floats add
 *  false precision to a number nobody reads below the millisecond. */
const durationSchema = z.number().int().nonnegative();

// =============================================================================
// CATALOG
// =============================================================================

interface EventDefinition {
  /** Cloud Logging severity this event is emitted at. */
  readonly severity: Severity;
  /** What the event means, and when it fires. Read by whoever is deciding
   *  whether a dashboard number is measuring what they think it is. */
  readonly description: string;
  /** Accepted fields. Unknown keys are stripped rather than rejected, so a
   *  stale call site degrades to a thinner event instead of a lost one —
   *  and so nobody can write arbitrary columns into the BigQuery sink. */
  readonly params: z.AnyZodObject;
  /**
   * Which params hold prose rather than a declared identifier.
   *
   * Only these get the sanitizer's opaque-run redaction, which replaces any
   * run of 24+ word characters with `[redacted]`. That rule is right for a
   * string an exception wrote and wrong for a field whose job is to carry a
   * long token — on an identifier it silently deletes the value while
   * leaving the column populated. Listing free text rather than listing
   * identifiers means a new field is never accidentally under-redacted.
   */
  readonly freeText?: readonly string[];
}

export const EVENT_CATALOG = {
  // --- Visits ----------------------------------------------------------
  web_session_started: {
    severity: 'INFO',
    description:
      'Once per sessionStorage id, on the first page of a tab. A reload ' +
      'within the same tab does not re-fire it; a new tab does.',
    params: z.object({
      entry_path: z.string(),
      /** Host only — never the full referring URL. */
      referrer: z.string(),
    }),
  },

  web_page_viewed: {
    severity: 'INFO',
    description:
      'Every route change, including the first render of the entry page. ' +
      '`previous_path` and `previous_dwell_ms` describe the page being ' +
      'LEFT, and are both absent on the first view of a page load. Dwell ' +
      'counts visible time only and is bounded — see durationSchema.',
    params: z.object({
      previous_path: z.string().optional(),
      previous_dwell_ms: durationSchema.optional(),
    }),
  },

  web_session_ended: {
    severity: 'INFO',
    description:
      'On visibilitychange -> hidden. Fires more than once per session if ' +
      'the visitor switches tabs and returns, so take MAX(duration_ms) per ' +
      'session_id rather than summing. `duration_ms` is the engaged ' +
      'session length; `dwell_ms` is time on the current `path` only, and ' +
      'is what gives the exit page a dwell figure — the exit page has no ' +
      'following web_page_viewed to derive one from.',
    params: z.object({
      duration_ms: durationSchema,
      dwell_ms: durationSchema.optional(),
    }),
  },

  // --- Interactions ----------------------------------------------------
  web_cta_clicked: {
    severity: 'INFO',
    description:
      'A call-to-action was activated. `cta_id` is a stable identifier ' +
      'assigned at the call site, never the button label — labels get ' +
      'reworded by copy changes and silently break the funnel. `placement` ' +
      'is WHERE on the page it was, kept as its own field so "which CTA" ' +
      'and "which placement converts" stay two separate GROUP BYs instead ' +
      'of one string encoding two dimensions.',
    params: z.object({
      cta_id: z.string(),
      placement: z.string(),
    }),
  },

  web_form_submitted: {
    severity: 'INFO',
    description:
      'A form submission completed, succeeded or not. Field contents are ' +
      'never included — only which form, how it ended, how long the round ' +
      'trip took, and (pitch-deck gate only) which recipient unlocked it.',
    params: z.object({
      form_id: z.string(),
      outcome: outcomeSchema,
      duration_ms: durationSchema.optional(),
      /**
       * Which recipient a shared, gated document was unlocked by.
       *
       * The one identity field this site emits, and it exists because
       * "did this investor open the deck?" is the whole question a pitch
       * deck is instrumented to answer. Present only on
       * `form_id: "pitch_deck_gate"` with `outcome: "succeeded"`.
       *
       * This is the deck's per-recipient **watermark** — the label already
       * rendered across the slides that recipient is looking at — and not
       * the password that produced it. The two identify the same recipient,
       * so the analytics value is identical, but the watermark is not a
       * working credential: a password here would put deck access into
       * every log reader's hands and into the BigQuery sink permanently.
       *
       * Never set on a failed attempt. What someone typed into a password
       * box is not knowable to be theirs — password reuse means a failed
       * attempt can carry a credential belonging to an entirely different
       * system, and it would be captured forever.
       */
      deck_recipient: z.string().optional(),
    }),
  },

  // --- Errors ----------------------------------------------------------
  web_error: {
    severity: 'ERROR',
    description:
      'A client-side error. Emitted at severity ERROR so it reaches the ' +
      'error charts. The message is redacted and truncated; the full stack ' +
      'trace goes to Cloud Error Reporting instead, which groups on it. ' +
      'Being the only ERROR event here has a consequence worth knowing: ' +
      'these rows land in the run_googleapis_com_stderr sink table, not ' +
      'stdout with the other five, so a query or schema check pointed at ' +
      'stdout reports no web errors indefinitely and is wrong.',
    params: z.object({
      error_category: errorCategorySchema,
      message: z.string(),
    }),
    /* The only prose in the catalog: an exception wrote it, so it can quote
       user input and must keep the opaque-run pass. */
    freeText: ['message'],
  },
} satisfies Record<string, EventDefinition>;

// =============================================================================
// DERIVED TYPES
// =============================================================================

/** Every valid event name. Misspellings fail at compile time. */
export type WebEventName = keyof typeof EVENT_CATALOG;

/** The payload a given event accepts, inferred from its schema. */
export type ParamsFor<N extends WebEventName> = z.infer<
  (typeof EVENT_CATALOG)[N]['params']
>;

/** Names as a runtime array — used by the README generator and by anything
 *  that needs to assert the emitted set matches the agreed set. */
export const EVENT_NAMES = Object.keys(EVENT_CATALOG) as WebEventName[];

/**
 * Params of `name` that hold prose, and so may be opaque-run redacted.
 *
 * A function rather than a direct property read because `satisfies` keeps
 * each entry's literal type, so `freeText` is absent from the union for
 * every event that does not declare it. Widening to `EventDefinition` once,
 * here, beats an `as` at the call site or a noisy `freeText: []` on entries
 * that have no prose in them.
 */
export function freeTextParamsFor(name: WebEventName): readonly string[] {
  const definition: EventDefinition = EVENT_CATALOG[name];
  return definition.freeText ?? [];
}
